import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import {
  Bell,
  Calendar as CalendarIcon,
  RefreshCw,
  Moon,
  Sun,
  CalendarCheck,
  MessageCircle,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import DateTimeHeader from "../components/DateTimeHeader";
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { usePreference } from '../contexts/PreferenceContext';
import ProfileModal from "../components/ProfileModal";
import Dropdown from "../components/Dropdown";
import CreateMeeting from "./CreateMeeting";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import NavBar from "../components/NavBar";
import { useMeeting } from "../contexts/MeetingContext";

const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const events: { [date: string]: any[] } = {};

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  // Returns 0 (Monday) to 6 (Sunday)
  const d = new Date(year, month, 1);
  return (d.getDay() + 6) % 7;
}

// Timezone handling utility
function convertToLocalDate(dateString: string, timeString?: string) {
  try {
    const date = new Date(dateString);
    if (timeString) {
      const [hours, minutes] = timeString.split(':');
      date.setHours(parseInt(hours), parseInt(minutes));
    }
    return date;
  } catch (error) {
    console.error('Error converting date:', error);
    return new Date(dateString);
  }
}

function getWeekDays(date: Date) {
  const startOfWeek = new Date(date);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  startOfWeek.setDate(diff);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    days.push(day);
  }
  return days;
}

function getDayHours() {
  const hours = [];
  for (let i = 0; i < 24; i++) {
    hours.push(i);
  }
  return hours;
}

// function formatTime(hour: number, tz: string) {
//   return dayjs().tz(tz).hour(hour).minute(0).format('HH:00');
// }

function getCurrentTimePosition() {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const totalMinutes = hours * 60 + minutes;

  // Calculate position as percentage (assuming 24-hour day)
  const startOfDay = 0; // 00:00
  const endOfDay = 24 * 60; // 24:00
  const position =
    ((totalMinutes - startOfDay) / (endOfDay - startOfDay)) * 100;

  return Math.max(0, Math.min(100, position));
}

const Calendar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState<string>("");
  const [showProfile, setShowProfile] = useState(false);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [meetingsError, setMeetingsError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  // const [darkMode, setDarkMode] = useState(false);
  const [viewMode, setViewMode] = useState<"month" | "week" | "day">("month");
  const [selectedWeek, setSelectedWeek] = useState<Date>(new Date());
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);
  const [selectedMeetingForDetails, setSelectedMeetingForDetails] =
    useState<any>(null);
  // console.log("selectedMeetingForDetails", selectedMeetingForDetails);

  const [meetingDetailsTab, setMeetingDetailsTab] = useState<
    "details" | "security"
  >("details");
  const [isEditing, setIsEditing] = useState(false);
  const [showMeetingOptionsDropdown, setShowMeetingOptionsDropdown] =
    useState(false);
  const [recurringInstanceCache, setRecurringInstanceCache] = useState<Map<string, any[]>>(new Map());
  const [showCancelMeetingModal, setShowCancelMeetingModal] = useState(false);
  const [securitySettings, setSecuritySettings] = useState({
    requirePassword: false,
    password: "meeting123",
    waitingRoom: true,
    muteOnEntry: false,
    allowRecording: true,
    moderators: ["john.smith@company.com"],
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { formatTime, formatDate } = usePreference();

  // Add MeetingContext
  const {
    meetings: contextMeetings,
    loading: contextLoading,
    error: contextError,
    fetchAllMeetings,
    cancelMeeting,
    editMeeting,
    joinMeeting
  } = useMeeting();

  // console.log("meeting in calendar", contextMeetings);




  // Notification state variables
  const initialNotifications = [
    {
      id: 1,
      icon: <CalendarCheck className="text-blue-400" size={18} />,
      text: "Meeting with Alex at 2:00 PM",
    },
    {
      id: 2,
      icon: <CalendarCheck className="text-green-400" size={18} />,
      text: "Design Review scheduled for tomorrow",
    },
    {
      id: 3,
      icon: <MessageCircle className="text-yellow-400" size={18} />,
      text: "New message from John",
    },
    {
      id: 4,
      icon: <AlertCircle className="text-red-400" size={18} />,
      text: "Your password will expire soon",
    },
  ];
  const [notifications, setNotifications] = useState(initialNotifications);
  const [clearedNotifications, setClearedNotifications] = useState<any[]>([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsTab, setNotificationsTab] = useState<
    "current" | "cleared"
  >("current");
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    time: "",
    recurring: "",
    host_name: "",
    room: "",
    status: "",
    participants: [],
    date: new Date(),
    startTime: "10:00 AM",
    endTime: "11:00 AM",
  });

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(
        now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
      setCurrentTime(now);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    setLoadingMeetings(true);
    fetchAllMeetings()
      .then(() => {
        setMeetingsError(null);
      })
      .catch((err) => {
        setMeetingsError("Failed to load meetings");
      })
      .finally(() => setLoadingMeetings(false));
  }, []);

  // Process meetings and generate recurring instances with performance optimization
  useEffect(() => {
    if (contextMeetings && contextMeetings.length > 0) {
      const startTime = performance.now();
      const processedMeetings: any[] = [];
      let recurringCount = 0;
      let totalInstances = 0;

      // Process meetings in chunks to avoid blocking the UI
      const processMeetings = (startIndex: number) => {
        const endIndex = Math.min(startIndex + 50, contextMeetings.length); // Process 50 meetings at a time

        for (let i = startIndex; i < endIndex; i++) {
          const meeting = contextMeetings[i];

          // Filter out canceled meetings
          if (!meeting.canceled_meeting) {
            processedMeetings.push(meeting);
            if (meeting.is_recurring) recurringCount++;
          }
        }

        if (endIndex < contextMeetings.length) {
          // Continue processing remaining meetings
          setTimeout(() => processMeetings(endIndex), 0);
        } else {
          // All meetings processed
          setMeetings(processedMeetings);
          const endTime = performance.now();
          console.log(`Processed ${contextMeetings.length} meetings in ${endTime - startTime}ms (${recurringCount} recurring, ${totalInstances} instances generated)`);
        }
      };

      processMeetings(0);
    }
  }, [contextMeetings]);

  // Handle click outside date picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  // Meeting modal handlers
  const handleMeetingClick = (meeting: any) => {
    setSelectedMeetingForDetails(meeting);
    setShowMeetingDetails(true);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    if (!selectedMeetingForDetails) return;
    setIsEditing(true);
    setEditForm({
      title: selectedMeetingForDetails.title || "",
      description:
        selectedMeetingForDetails.description ||
        "Weekly team sync to discuss project updates, address any blockers, and plan upcoming tasks. All team members are encouraged to come prepared with their updates.",
      time: selectedMeetingForDetails.time || "9:00 AM - 10:00 AM",
      recurring: selectedMeetingForDetails.recurring || "Weekly",
      host_name: selectedMeetingForDetails.created_by.first_name || "John Smithss",
      room: selectedMeetingForDetails.room || "Room 1432",
      status: selectedMeetingForDetails.status || "Scheduled",
      participants: selectedMeetingForDetails.participants || [],
      date: new Date(),
      startTime: "10:00 AM",
      endTime: "11:00 AM",
    });
  };

  const handleSaveEdit = async () => {
    if (!selectedMeetingForDetails) return;

    try {
      // Convert form data to the format expected by the API
      const updates = {
        title: editForm.title,
        description: editForm.description,
        meetingDate: editForm.date,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        // Add other fields as needed
      };

      await editMeeting(selectedMeetingForDetails.id, updates);

      // After editing, refresh the meetings list
      fetchAllMeetings();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating meeting:", error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      title: "",
      description: "",
      time: "",
      recurring: "",
      host_name: "",
      room: "",
      status: "",
      participants: [],
      date: new Date(),
      startTime: "10:00 AM",
      endTime: "11:00 AM",
    });
  };

  const handleCancelMeeting = async () => {
    if (selectedMeetingForDetails) {
      try {
        await cancelMeeting(selectedMeetingForDetails.id);
        // After cancellation, refresh the meetings list
        fetchAllMeetings();
        setShowCancelMeetingModal(false);
        setShowMeetingDetails(false);
      } catch (error) {
        console.error("Error canceling meeting:", error);
      }
    }
  };

  // Generate time options in 15-minute intervals with AM/PM format
  const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = String(minute).padStart(2, "0");
    return `${displayHour}:${displayMinute} ${period}`;
  });

  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfWeek = getFirstDayOfWeek(year, month);

  // Build calendar grid
  const days: Array<{ day: number | null; date: string }> = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    days.push({ day: null, date: "" });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
      d
    ).padStart(2, "0")}`;
    days.push({ day: d, date: dateStr });
  }
  while (days.length % 7 !== 0) {
    days.push({ day: null, date: "" });
  }

  // Map meetings to dates
  const meetingEvents: { [date: string]: any[] } = {};

  const generateRecurringInstances = (meeting: any, currentDate: Date): any[] => {
    if (!meeting.is_recurring || !meeting.recurring_start_date || !meeting.recurring_end_date) {
      return [];
    }

    // Check cache first
    const cacheKey = `${meeting.id}-${currentDate.getFullYear()}-${currentDate.getMonth()}`;
    const cachedInstances = recurringInstanceCache.get(cacheKey);
    if (cachedInstances) {
      return cachedInstances;
    }

    try {
      const instances: any[] = [];
      const startDate = new Date(meeting.recurring_start_date);
      const endDate = new Date(meeting.recurring_end_date);
      const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const currentMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      // Only generate instances for the current month for performance
      const effectiveStart = startDate > currentMonthStart ? startDate : currentMonthStart;
      const effectiveEnd = endDate < currentMonthEnd ? endDate : currentMonthEnd;

      // Validate date ranges
      if (effectiveStart > effectiveEnd) {
        return [];
      }

      let currentInstance = new Date(effectiveStart);
      const maxInstances = 100; // Prevent infinite loops
      let instanceCount = 0;

      while (currentInstance <= effectiveEnd && instanceCount < maxInstances) {
        let shouldCreateInstance = false;

        switch (meeting.repeat_status) {
          case "daily":
            shouldCreateInstance = true;
            break;
          case "weekly":
            if (meeting.repeat_days && meeting.repeat_days.length > 0) {
              const dayName = currentInstance.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
              shouldCreateInstance = meeting.repeat_days.includes(dayName);
            } else {
              // If no specific days, repeat on the same day of week as start date
              const startDay = startDate.getDay();
              shouldCreateInstance = currentInstance.getDay() === startDay;
            }
            break;
          case "bi-weekly":
            const weeksDiff = Math.floor((currentInstance.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
            shouldCreateInstance = weeksDiff % 2 === 0 && currentInstance.getDay() === startDate.getDay();
            break;
          case "monthly":
            shouldCreateInstance = currentInstance.getDate() === startDate.getDate();
            break;
          case "yearly":
            shouldCreateInstance = currentInstance.getDate() === startDate.getDate() &&
              currentInstance.getMonth() === startDate.getMonth();
            break;
          default:
            shouldCreateInstance = false;
        }

        if (shouldCreateInstance) {
          const instance = { ...meeting };
          instance.meeting_date = currentInstance.toISOString().split("T")[0];
          instance.id = `${meeting.id}-${currentInstance.toISOString().split("T")[0]}`;
          instance.is_recurring = false; // Mark instance as non-recurring to prevent infinite recursion
          instance.is_recurring_instance = true; // Add flag to identify this as a recurring instance
          instances.push(instance);
          instanceCount++;
        }

        // Move to next day
        currentInstance.setDate(currentInstance.getDate() + 1);
      }

      // Cache the results
      setRecurringInstanceCache(prev => {
        const newCache = new Map(prev);
        newCache.set(cacheKey, instances);
        return newCache;
      });

      // Update debug info
      console.log(`Generated ${instances.length} instances for meeting ${meeting.id} (${meeting.title}) with repeat_status: ${meeting.repeat_status}`);
      return instances;
    } catch (error) {
      console.error("Error generating recurring instances:", error);
      return [];
    }
  };

  meetings.forEach((mtg) => {
    // Handle recurring meetings
    if (mtg.is_recurring) {
      const recurringInstances = generateRecurringInstances(mtg, new Date(year, month));
      recurringInstances.forEach((instance) => {
        const [yStr, mStr, dStr] = instance.meeting_date.split("T")[0].split("-");
        const dateObj = new Date(parseInt(yStr, 10), parseInt(mStr, 10) - 1, parseInt(dStr, 10));
        const dateStr = `${dateObj.getFullYear()}-${String(
          dateObj.getMonth() + 1
        ).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;

        if (!meetingEvents[dateStr]) meetingEvents[dateStr] = [];
        meetingEvents[dateStr].push(instance);
      });
    }

    // Handle regular meetings
    // Use meeting_date and start_time if available, else created_at
    let dateObj;
    if (mtg.meeting_date) {
      // Convert to local date to avoid timezone issues
      const [yStr, mStr, dStr] = mtg.meeting_date.split('T')[0].split('-');
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10);
      const d = parseInt(dStr, 10);
      dateObj = new Date(y, m - 1, d);
    } else {
      dateObj = new Date(mtg.created_at);
    }

    const dateStr = `${dateObj.getFullYear()}-${String(
      dateObj.getMonth() + 1
    ).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;

    if (!meetingEvents[dateStr]) meetingEvents[dateStr] = [];
    meetingEvents[dateStr].push(mtg);
  });

  // Get today's date string for comparison
  const { timezone } = usePreference();
  const resolvedTimezone = timezone === "automatic"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : timezone;
  const today = dayjs().tz(resolvedTimezone).toDate();
  const todayStr = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  dayjs.extend(utcPlugin);
  dayjs.extend(timezonePlugin);


  // Get today's date string for comparison
  const todayStrForComparison = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const formatHour24 = (hour: number) => {
    return `${String(hour).padStart(2, "0")}:00`;
  };

  // Handle join meeting with JWT token
  const handleJoinMeeting = async (meeting: any) => {
    try {
      const result = await joinMeeting(meeting.id);
      console.log("tokennn", result);

      const meetingUrl = result.meeting.meeting_link;
      window.open(meetingUrl, "_blank");
    } catch (error: any) {
      console.error("Failed to join meeting:", error);
      alert("Failed to join meeting. Please try again.");
    }
  };

  return (
    <div
      className={`min-h-screen ${darkMode
        ? "bg-gradient-to-br from-[#181824] to-[#232336]"
        : "bg-gradient-to-br from-gray-50 to-blue-50"
        }`}
    >
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main
        className={`flex-1 px-6 py-8 md:px-12 md:py-10 transition-all duration-200 ${collapsed ? "ml-20" : "ml-20 md:ml-52"
          }`}
      >
        {/* Top Bar */}
        <NavBar />

        {/* Page Title */}
        <div className="mb-8 mt-12">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
            <div>
              <h2
                className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Calendar
              </h2>
            </div>
          </div>
        </div>

        {/* Calendar Container */}
        <div
          className={`w-full rounded-3xl shadow-2xl p-6 lg:p-8 relative mx-auto border overflow-x-auto ${darkMode
            ? "bg-gradient-to-br from-[#2c2c3a] to-[#1a1a2e] border-white/10"
            : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
            }`}
        >
          {/* Modern Header */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-8">
            <div className="flex items-center lg:justify-start justify-center relative">
              {showDatePicker && (
                <div
                  ref={datePickerRef}
                  className={`absolute left-0 top-12 z-50 rounded-2xl shadow-2xl p-6 flex flex-col sm:min-w-[280px] min-w-[150px] border backdrop-blur-xl ${darkMode
                    ? "bg-gradient-to-br from-[#232336]/95 to-[#1a1a2e]/95 border-white/20"
                    : "bg-gradient-to-br from-white/95 to-gray-50/95 border-gray-200"
                    }`}
                >
                  {/* Compact Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
                      <h3
                        className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Select Date
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className={`p-1.5 rounded-lg transition-all duration-300 hover:scale-110 ${darkMode
                        ? "text-white/60 hover:text-white hover:bg-white/10"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Compact Month Selector */}
                  <div className="mb-4">
                    <label
                      className={`block text-sm font-semibold mb-3 ${darkMode ? "text-white/80" : "text-gray-700"
                        }`}
                    >
                      Month
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {months.map((m, idx) => (
                        <button
                          key={m}
                          onClick={() => {
                            if (viewMode === "month") {
                              setMonth(idx);
                            } else if (viewMode === "week") {
                              const newWeek = new Date(selectedWeek);
                              newWeek.setMonth(idx);
                              setSelectedWeek(newWeek);
                            } else if (viewMode === "day") {
                              const newDay = new Date(selectedDay);
                              newDay.setMonth(idx);
                              setSelectedDay(newDay);
                            }
                            setShowDatePicker(false);
                          }}
                          className={`px-3 py-2 rounded-xl transition-all duration-300 font-semibold hover:scale-105 ${(viewMode === "month" && idx === month) ||
                            (viewMode === "week" &&
                              idx === selectedWeek.getMonth()) ||
                            (viewMode === "day" &&
                              idx === selectedDay.getMonth())
                            ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg transform scale-105"
                            : darkMode
                              ? "text-white/80 hover:bg-white/10 hover:text-white border border-white/10"
                              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-gray-200"
                            }`}
                        >
                          {m.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Compact Year Selector */}
                  <div className="mb-4">
                    <label
                      className={`block text-sm font-semibold mb-2 ${darkMode ? "text-white/80" : "text-gray-700"
                        }`}
                    >
                      Year
                    </label>
                    <div className="relative">
                      <select
                        value={
                          viewMode === "month"
                            ? year
                            : viewMode === "week"
                              ? selectedWeek.getFullYear()
                              : selectedDay.getFullYear()
                        }
                        onChange={(e) => {
                          const newYear = Number(e.target.value);
                          if (viewMode === "month") {
                            setYear(newYear);
                          } else if (viewMode === "week") {
                            const newWeek = new Date(selectedWeek);
                            newWeek.setFullYear(newYear);
                            setSelectedWeek(newWeek);
                          } else if (viewMode === "day") {
                            const newDay = new Date(selectedDay);
                            newDay.setFullYear(newYear);
                            setSelectedDay(newDay);
                          }
                        }}
                        className={`w-full bg-transparent text-base font-bold focus:ring-2 focus:ring-pink-500 rounded-lg px-3 py-2 border-2 transition-all duration-300 ${darkMode
                          ? "text-white border-white/20 focus:border-pink-500 bg-white/5"
                          : "text-gray-900 border-gray-200 focus:border-pink-500 bg-white/50"
                          }`}
                      >
                        {Array.from(
                          { length: 11 },
                          (_, i) => currentYear - 5 + i
                        ).map((y) => (
                          <option
                            key={y}
                            value={y}
                            className={`${darkMode
                              ? "bg-[#232336] text-white"
                              : "bg-white text-gray-900"
                              }`}
                          >
                            {y}
                          </option>
                        ))}
                      </select>
                      <div
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${darkMode ? "text-white/50" : "text-gray-500"
                          }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Compact Quick Actions */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
                    <button
                      onClick={() => {
                        const today = new Date();
                        if (viewMode === "month") {
                          setYear(today.getFullYear());
                          setMonth(today.getMonth());
                        } else if (viewMode === "week") {
                          setSelectedWeek(today);
                        } else if (viewMode === "day") {
                          setSelectedDay(today);
                        }
                        setShowDatePicker(false);
                      }}
                      className={`w-full px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105 bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg hover:shadow-xl`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        Go to Today
                      </div>
                    </button>
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  if (viewMode === "month") {
                    setMonth((m) => (m === 0 ? 11 : m - 1));
                    // Clear recurring instance cache when changing months
                    setRecurringInstanceCache(new Map());
                  } else if (viewMode === "week") {
                    const newWeek = new Date(selectedWeek);
                    newWeek.setDate(selectedWeek.getDate() - 7);
                    setSelectedWeek(newWeek);
                  } else if (viewMode === "day") {
                    const newDay = new Date(selectedDay);
                    newDay.setDate(selectedDay.getDate() - 1);
                    setSelectedDay(newDay);
                  }
                }}
                className={`px-3 py-2.5 rounded-xl transition-all duration-300 hover:scale-110 ${darkMode
                  ? "text-white/70 hover:text-white hover:bg-white/10"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                  }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                className={`text-center px-8 py-2.5 rounded-xl transition-all duration-300 border-2 hover:scale-105 backdrop-blur-sm min-w-[180px] ${darkMode
                  ? "hover:bg-white/10 border-white/20 hover:border-white/30 bg-white/5"
                  : "hover:bg-gray-100 border-gray-200 hover:border-gray-300 bg-white/50"
                  }`}
                onClick={() => setShowDatePicker((v) => !v)}
              >
                <div className="flex items-center justify-center gap-3">
                  {viewMode === "month" && (
                    <>
                      <div
                        className={`text-lg font-bold tracking-wide ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {months[month]}
                      </div>
                      <div
                        className={`text-lg font-bold ${darkMode ? "text-white/60" : "text-gray-500"
                          }`}
                      >
                        {year}
                      </div>
                    </>
                  )}
                  {viewMode === "week" && (
                    <div
                      className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {selectedWeek.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      -{" "}
                      {new Date(
                        selectedWeek.getTime() + 6 * 24 * 60 * 60 * 1000
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  )}
                  {viewMode === "day" && (
                    <div
                      className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {selectedDay.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  )}
                </div>
              </button>
              <button
                onClick={() => {
                  if (viewMode === "month") {
                    setMonth((m) => (m === 11 ? 0 : m + 1));
                    // Clear recurring instance cache when changing months
                    setRecurringInstanceCache(new Map());
                  } else if (viewMode === "week") {
                    const newWeek = new Date(selectedWeek);
                    newWeek.setDate(selectedWeek.getDate() + 7);
                    setSelectedWeek(newWeek);
                  } else if (viewMode === "day") {
                    const newDay = new Date(selectedDay);
                    newDay.setDate(selectedDay.getDate() + 1);
                    setSelectedDay(newDay);
                  }
                }}
                className={`px-3 py-2.5 rounded-xl transition-all duration-300 hover:scale-110 ${darkMode
                  ? "text-white/70 hover:text-white hover:bg-white/10"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                  }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
            <div className="flex lg:flex-row flex-col items-center gap-4">
              {/* Modern View Mode Toggle */}
              <div
                className={`flex rounded-2xl p-1.5 backdrop-blur-sm ${darkMode
                  ? "bg-white/10 border border-white/20"
                  : "bg-gray-100/80 border border-gray-200"
                  }`}
              >
                <button
                  onClick={() => {
                    setViewMode("month");
                    // Clear recurring instance cache when changing view mode
                    setRecurringInstanceCache(new Map());
                  }}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${viewMode === "month"
                    ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg transform scale-105"
                    : darkMode
                      ? "text-white/70 hover:text-white hover:bg-white/10"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                    }`}
                >
                  Month
                </button>
                <button
                  onClick={() => {
                    setViewMode("week");
                    // Clear recurring instance cache when changing view mode
                    setRecurringInstanceCache(new Map());
                  }}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${viewMode === "week"
                    ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg transform scale-105"
                    : darkMode
                      ? "text-white/70 hover:text-white hover:bg-white/10"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                    }`}
                >
                  Week
                </button>
                <button
                  onClick={() => {
                    setViewMode("day");
                    // Clear recurring instance cache when changing view mode
                    setRecurringInstanceCache(new Map());
                  }}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${viewMode === "day"
                    ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg transform scale-105"
                    : darkMode
                      ? "text-white/70 hover:text-white hover:bg-white/10"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                    }`}
                >
                  Day
                </button>
              </div>
              <button
                onClick={() => {
                  const today = new Date();
                  if (viewMode === "month") {
                    setYear(today.getFullYear());
                    setMonth(today.getMonth());
                  } else if (viewMode === "week") {
                    setSelectedWeek(today);
                  } else if (viewMode === "day") {
                    setSelectedDay(today);
                  }
                  // Clear recurring instance cache when going to today
                  setRecurringInstanceCache(new Map());
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${darkMode
                  ? "bg-white/10 text-white hover:bg-white/20"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Today
              </button>
              <button
                onClick={() => setShowCreateMeeting(true)}
                className="bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-semibold px-4 py-3 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Schedule Meeting
                </div>
              </button>
            </div>
          </div>
          {/* Calendar Content */}
          {viewMode === "month" && (
            <div className="hidden md:block">
              <>

                {/* Modern Weekdays Header */}
                <div className="grid grid-cols-7 text-center mb-6 overflow-x-auto min-w-[420px]">
                  {weekdays.map((day) => (
                    <div
                      key={day}
                      className={`py-4 text-sm font-bold tracking-wide min-w-[60px] ${darkMode ? "text-white/70" : "text-gray-700"
                        }`}
                    >
                      {day.slice(0, 3)}
                    </div>
                  ))}
                </div>
                {/* Modern Days Grid */}
                <div className="grid grid-cols-7 gap-3 overflow-x-auto min-w-[420px] p-2">
                  {days.map((cell, idx) => {
                    const eventList: any[] = meetingEvents[cell.date] || [];
                    const isToday = cell.date === todayStrForComparison;
                    return (
                      <div
                        key={idx}
                        className={`aspect-square flex flex-col items-center justify-center border-2 relative rounded-2xl transition-all duration-300 hover:scale-105 cursor-pointer backdrop-blur-sm  min-w-[60px]
                  ${cell.day
                            ? darkMode
                              ? "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10"
                              : "bg-white/80 border-gray-200 hover:border-gray-300 hover:bg-white shadow-lg"
                            : darkMode
                              ? "bg-[#232336]/50 border-white/5"
                              : "bg-gray-50/50 border-gray-100"
                          }
                      ${eventList.length > 0 && !isToday
                            ? darkMode
                              ? "border-purple-500/50 bg-purple-500/10 shadow-lg shadow-purple-500/20"
                              : "border-purple-300 bg-purple-50 shadow-lg shadow-purple-500/20"
                            : ""
                          }
                  ${isToday
                            ? darkMode
                              ? "!border-red-400 !bg-red-500/20 shadow-lg shadow-red-500/30"
                              : "!border-red-500 !bg-red-50 shadow-lg shadow-red-500/20"
                            : ""
                          }`}
                        onClick={() => {
                          if (cell.day) {
                            setSelectedDate(cell.date);
                            setShowMeetingModal(true);
                          }
                        }}
                      >
                        {cell.day && (
                          <>
                            <div
                              className={`absolute top-2 left-2 text-sm font-semibold ${darkMode ? "text-white/80" : "text-gray-700"
                                }`}
                            >
                              {cell.day}
                            </div>

                            {/* Event blocks with dividers */}
                            {eventList.length > 0 && (
                              <div className="flex flex-col items-center justify-center mt-4 w-full px-1.5 space-y-0.5 xl:h-auto h-8 overflow-auto">
                                {eventList.slice(0, 3).map((event, idx) => {
                                  // Combine meeting_date + start_time to make a valid Date
                                  const startDateTime = event.meeting_date && event.start_time
                                    ? new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`)
                                    : null;

                                  // Determine if this is a recurring meeting instance
                                  const isRecurringMeeting = event.is_recurring || event.is_recurring_instance;

                                  return (
                                    <div
                                      key={event.id || idx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMeetingClick(event);
                                      }}
                                      className={`w-full p-1.5 rounded-md cursor-pointer transition-all duration-200 hover:scale-105 ${isRecurringMeeting
                                        ? darkMode
                                          ? "bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20"
                                          : "bg-purple-50 border border-purple-200 hover:bg-purple-100"
                                        : darkMode
                                          ? "bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20"
                                          : "bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                                        }`}
                                    >
                                      <div className="flex items-center gap-1">
                                        {isRecurringMeeting && (
                                          <svg
                                            className={`w-3 h-3 ${darkMode ? "text-purple-400" : "text-purple-600"
                                              }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                            />
                                          </svg>
                                        )}
                                        <div
                                          className={`text-xs font-semibold truncate ${darkMode ? "text-white/90" : "text-gray-800"
                                            }`}
                                        >
                                          {event.title || event.label || "Meeting"}
                                        </div>
                                      </div>
                                      {startDateTime && (
                                        <div
                                          className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}
                                        >
                                          {startDateTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                        </div>
                                      )}
                                      {isRecurringMeeting && (
                                        <div
                                          className={`text-xs ${darkMode ? "text-purple-400/70" : "text-purple-600/70"
                                            }`}
                                        >
                                          Recurring
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}

                                {eventList.length > 3 && (
                                  <div
                                    className={`text-xs font-medium text-center ${darkMode
                                      ? "text-white/60"
                                      : "text-gray-600"
                                      }`}
                                  >
                                    +{eventList.length - 3} more
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            </div>
          )}
          {/* MOBILE MONTH VIEW UI */}
          {viewMode === "month" && (
            <div className="block md:hidden w-full">
              <div className="rounded-3xl shadow-xl ">
                {/* Days of Week */}
                <div className="grid grid-cols-7 text-center mb-2">
                  {weekdays.map(day => (
                    <div key={day} className="text-xs font-bold text-gray-600">
                      {day.slice(0, 1)}
                    </div>
                  ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {days.map((cell, idx) => {
                    const dateStr = cell.date;
                    const events = meetingEvents[dateStr] || [];
                    const hasMeeting = events.length > 0;
                    const isToday = dateStr === todayStr;

                    return (
                      <button
                        key={idx}
                        disabled={!cell.day}
                        onClick={() => {
                          setSelectedDate(dateStr);
                          setShowMeetingModal(true);
                        }}
                        className={`
    h-[clamp(1.8rem,4.5vw,2.4rem)]
    w-[clamp(1.8rem,4.5vw,2.4rem)]
    flex items-center justify-center 
    rounded-xl mx-auto text-sm font-semibold
    ${!cell.day ? "opacity-30" : ""}
    ${hasMeeting ? "bg-blue-500 text-white" : ""}
    ${isToday && !hasMeeting ? "border-2 border-blue-500 text-blue-600" : ""}
    ${!hasMeeting && !isToday ? "bg-gray-100 text-gray-900 opacity-30" : ""}
  `}
                      >
                        {cell.day}
                      </button>

                    );
                  })}
                </div>

              </div>
            </div>
          )}


          {viewMode === "week" && (
            <div className="hidden md:block">
              <div className="space-y-4">
                {/* Week Header */}
                <div className="grid grid-cols-8 gap-2 mb-4 min-w-[420px]">
                  <div
                    className={`py-3 text-sm font-semibold text-center ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    Time
                  </div>
                  {getWeekDays(selectedWeek).map((day, idx) => {
                    const dateStr = `${day.getFullYear()}-${String(
                      day.getMonth() + 1
                    ).padStart(2, "0")}-${String(day.getDate()).padStart(
                      2,
                      "0"
                    )}`;
                    const isToday = dateStr === todayStrForComparison;
                    return (
                      <div
                        key={idx}
                        className={`py-3 text-center border-2 rounded-xl transition-all duration-200 min-h-[60px] flex flex-col justify-center ${isToday
                          ? darkMode
                            // ? "border-red-500 bg-red-500/10"
                            // : "border-red-500 bg-red-50"

                            ? "border-red-500/50 bg-gradient-to-r from-red-500/10 to-pink-500/5 shadow-lg shadow-red-500/20"
                            : "border-red-500/50 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg shadow-red-500/10"
                          : darkMode
                            ? "border-white/10 bg-white/5"
                            : "border-gray-200 bg-white"
                          }`}
                      >
                        <div
                          className={`text-sm font-semibold ${darkMode ? "text-white/80" : "text-gray-700"
                            }`}
                        >
                          {weekdays[
                            day.getDay() === 0 ? 6 : day.getDay() - 1
                          ].slice(0, 3)}
                        </div>
                        <div
                          className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          {day.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Week Grid */}
                <div className="grid grid-cols-8 gap-2 relative min-w-[420px]">
                  {/* Time Indicator */}
                  {(() => {
                    const today = new Date();
                    const selectedWeekStart = new Date(selectedWeek);
                    selectedWeekStart.setDate(
                      selectedWeek.getDate() - selectedWeek.getDay() + 1
                    );
                    const selectedWeekEnd = new Date(selectedWeekStart);
                    selectedWeekEnd.setDate(selectedWeekStart.getDate() + 6);

                    const isTodayInSelectedWeek =
                      today >= selectedWeekStart && today <= selectedWeekEnd;
                    const currentHour = currentTime.getHours();
                    const currentMinute = currentTime.getMinutes();
                    const totalMinutes = currentHour * 60 + currentMinute;
                    const position = ((totalMinutes - 48) / (24 * 60)) * 100;
                    return isTodayInSelectedWeek ? (
                      <div
                        className="absolute left-0 right-0 z-10 pointer-events-none"
                        style={{
                          top: `${position}%`,
                          transform: "translateY(-50%)",
                        }}
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 rounded-full ${darkMode ? "bg-red-400" : "bg-red-500"
                              } shadow-lg`}
                          ></div>
                          <div
                            className={`ml-2 px-2 py-1 rounded-md text-xs font-semibold ${darkMode
                              ? "bg-red-500 text-white"
                              : "bg-red-500 text-white"
                              } shadow-lg`}
                          >
                            {currentTime.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          <div
                            className={`flex-1 h-0.5 ${darkMode ? "bg-red-400" : "bg-red-500"
                              } shadow-sm`}
                          ></div>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {getDayHours().map((hour) => (
                    <React.Fragment key={hour}>
                      <div
                        className={`py-2 text-sm font-medium text-center ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        {formatHour24(hour)}
                      </div>
                      {getWeekDays(selectedWeek).map((day, dayIdx) => {
                        const dateStr = `${day.getFullYear()}-${String(
                          day.getMonth() + 1
                        ).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
                        const dayEvents = meetingEvents[dateStr] || [];
                        const hourEvents = dayEvents.filter((event: any) => {
                          if (!event.start_time || !event.meeting_date) return false;
                          const eventDateTime = new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`);
                          const eventHour = eventDateTime.getHours();
                          return eventHour === hour;
                        });

                        const displayEvents = hourEvents.slice(0, 3);
                        const moreCount = Math.max(0, hourEvents.length - 3);

                        return (
                          <div
                            key={dayIdx}
                            className={`min-h-[60px] p-2 border-2 rounded-xl cursor-pointer transition-all duration-200 transform hover:scale-105  ${darkMode
                              ? "border-white/10 bg-white/5 hover:border-white/20 "
                              : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                            onClick={() => {
                              setSelectedDate(dateStr);
                              setShowMeetingModal(true);
                            }}
                          >
                            {displayEvents.map((event: any, eventIdx: number) => {
                              const startTime = event.meeting_date && event.start_time
                                ? new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`)
                                : null;
                              const isRecurringMeeting = event.is_recurring || event.is_recurring_instance;

                              return (
                                <div
                                  key={eventIdx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMeetingClick(event);
                                  }}
                                  className={`w-full p-1 rounded-md cursor-pointer transition-all duration-200 hover:scale-105 mb-1 ${isRecurringMeeting
                                    ? darkMode
                                      ? "bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20"
                                      : "bg-purple-50 border border-purple-200 hover:bg-purple-100"
                                    : darkMode
                                      ? "bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20"
                                      : "bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                                    }`}
                                >
                                  <div className="flex items-center gap-1">
                                    {isRecurringMeeting && (
                                      <svg
                                        className={`w-3 h-3 ${darkMode ? "text-purple-400" : "text-purple-600"
                                          }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                        />
                                      </svg>
                                    )}
                                    <div
                                      className={`text-xs font-semibold truncate ${darkMode ? "text-white/90" : "text-gray-800"
                                        }`}
                                    >
                                      {event.title || event.label || "Meeting"}
                                    </div>
                                  </div>
                                  {startTime && (
                                    <div
                                      className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}
                                    >
                                      {startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                  )}
                                  {isRecurringMeeting && (
                                    <div
                                      className={`text-xs ${darkMode ? "text-purple-400/70" : "text-purple-600/70"
                                        }`}
                                    >
                                      Recurring
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {moreCount > 0 && (
                              <div
                                className={`text-xs font-medium text-center mt-1 ${darkMode
                                  ? "text-white/60"
                                  : "text-gray-600"
                                  }`}
                              >
                                +{moreCount} more
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* MOBILE WEEK VIEW UI (GLASS MORPHISM) */}
          {viewMode === "week" && (
            <div className="block md:hidden p-3 w-full">

              {/* Week Strip */}
              <div className="flex justify-between mb-4 overflow-x-auto no-scrollbar px-1">
                {getWeekDays(selectedWeek).map((day, idx) => {
                  const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
                  const isToday = dateStr === todayStrForComparison;
                  const hasMeeting = (meetingEvents[dateStr] || []).length > 0;

                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedDate(dateStr)}
                      className={`
              flex flex-col items-center justify-center 
              min-w-[55px] px-3 py-2 mx-1 rounded-2xl
              backdrop-blur-md bg-white/20 border border-white/30
              shadow-[0_4px_10px_rgba(0,0,0,0.12)]
              transition-all duration-300
              ${isToday
                          ? "bg-blue-500/80 text-white  border-blue-300 shadow-blue-500/40"
                          : selectedDate === dateStr
                            ? "bg-blue-100/60 text-blue-700 border-blue-300"
                            : "text-gray-800 dark:text-white"}
            `}
                    >
                      <span className="text-[11px] font-medium">
                        {weekdays[(day.getDay() + 6) % 7].slice(0, 3)}
                      </span>

                      <span className="text-[18px] font-bold leading-4">
                        {day.getDate()}
                      </span>

                      {hasMeeting && (
                        <span className="mt-1 w-2 h-2 bg-blue-600 rounded-full shadow-md"></span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Glass Event Card Container */}
              <div
                className="
        rounded-3xl p-5 
        backdrop-blur-xl bg-white/10 border border-white/30
        shadow-[0_8px_30px_rgba(0,0,0,0.1)]
      "
              >
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 drop-shadow-sm">
                  Events for {selectedDate}
                </h3>

                {(meetingEvents[selectedDate ?? ""] || []).length === 0 && (
                  <p className="text-gray-700/70 dark:text-white/70 text-sm">No events today.</p>
                )}

                <div className="flex flex-col gap-3 mt-3">
                  {(meetingEvents[selectedDate ?? ""] || []).map((event, idx) => {
                    const startTime = event.meeting_date && event.start_time
                      ? new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`)
                      : null;

                    return (
                      <div
                        key={idx}
                        onClick={() => handleMeetingClick(event)}
                        className="
                p-4 rounded-2xl cursor-pointer
                backdrop-blur-lg bg-blue border border-blue-300/40
                shadow-md hover:shadow-lg transition-all
              "
                      >
                        <div className="font-semibold text-blue-900 dark:text-white drop-shadow-sm">
                          {event.title || "Meeting"}
                        </div>

                        {startTime && (
                          <div className="text-xs text-blue-700/80 dark:text-white mt-1">
                            {startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}

                        {(event.is_recurring || event.is_recurring_instance) && (
                          <div className="text-xs mt-1 font-semibold text-purple-600 dark:text-purple-300">
                            Recurring
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {viewMode === "day" && (
            <div className="hidden sm:block">
              <div className="space-y-6">
                {/* Enhanced Day Header */}
                <div
                  className={`relative overflow-hidden rounded-3xl border-2 transition-all duration-300 ${selectedDay.toDateString() === new Date().toDateString()
                    ? darkMode
                      ? "border-red-500/50 bg-gradient-to-r from-red-500/10 to-pink-500/5 shadow-lg shadow-red-500/20"
                      : "border-red-500/50 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg shadow-red-500/10"
                    : darkMode
                      ? "border-white/10 bg-gradient-to-r from-white/5 to-white/10"
                      : "border-gray-200 bg-gradient-to-r from-white to-gray-50"
                    }`}
                >
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-4 right-4 w-32 h-32 bg-gradient-to-br from-pink-500 to-red-500 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-4 left-4 w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-2xl"></div>
                  </div>

                  <div className="relative py-3 text-center min-h-[60px] flex flex-col justify-center">
                    <div
                      className={`text-sm font-semibold mb-2 ${darkMode ? "text-white/80" : "text-gray-700"
                        }`}
                    >
                      {selectedDay.toLocaleDateString("en-US", {
                        weekday: "long",
                      })}
                    </div>
                    <div
                      className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {selectedDay.getDate()}
                    </div>
                  </div>
                </div>

                {/* Enhanced Timeline */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2 relative">
                  {/* Time Tracking Indicator */}
                  {(() => {
                    const isToday =
                      selectedDay.toDateString() === new Date().toDateString();
                    const currentHour = currentTime.getHours();
                    const currentMinute = currentTime.getMinutes();
                    const totalMinutes = currentHour * 60 + currentMinute;
                    const hourHeight = 78; // Approximate height of each hour block
                    const minuteOffset = (currentMinute / 60) * hourHeight;
                    const position = currentHour * hourHeight + minuteOffset;

                    return isToday ? (
                      <div
                        className="absolute left-0 right-0 z-10 pointer-events-none"
                        style={{
                          top: `${position}px`,
                          transform: "translateY(-50%)",
                        }}
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 rounded-full ${darkMode ? "bg-red-400" : "bg-red-500"
                              } shadow-lg`}
                          ></div>
                          <div
                            className={`ml-2 px-2 py-1 rounded-md text-xs font-semibold ${darkMode
                              ? "bg-red-500 text-white"
                              : "bg-red-500 text-white"
                              } shadow-lg`}
                          >
                            {currentTime.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          <div
                            className={`flex-1 h-0.5 ${darkMode ? "bg-red-400" : "bg-red-500"
                              } shadow-sm`}
                          ></div>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {getDayHours().map((hour) => {
                    const dateStr = `${selectedDay.getFullYear()}-${String(
                      selectedDay.getMonth() + 1
                    ).padStart(2, "0")}-${String(selectedDay.getDate()).padStart(
                      2,
                      "0"
                    )}`;
                    const dayEvents = meetingEvents[dateStr] || [];
                    const hourEvents = dayEvents.filter((event: any) => {
                      if (!event.start_time || !event.meeting_date) return false;
                      const eventDateTime = new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`);
                      const eventHour = eventDateTime.getHours();
                      return eventHour === hour;
                    });

                    const isCurrentHour =
                      selectedDay.toDateString() === new Date().toDateString() &&
                      currentTime.getHours() === hour;

                    return (
                      <div
                        key={hour}
                        className={`relative group transition-all duration-300 ${isCurrentHour
                          ? darkMode
                            ? "bg-gradient-to-r from-red-500/10 to-pink-500/5 border-red-500/30"
                            : "bg-gradient-to-r from-red-50 to-pink-50 border-red-500/20"
                          : darkMode
                            ? "hover:bg-white/5 border-white/10"
                            : "hover:bg-gray-50 border-gray-200"
                          } border-2 rounded-xl p-3`}
                      >
                        {/* Current Hour Indicator */}
                        {isCurrentHour && (
                          <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        )}

                        <div className="flex items-start gap-3">
                          {/* Time Column */}
                          <div className="flex-shrink-0 w-16">
                            <div
                              className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {formatHour24(hour)}
                            </div>
                            <div
                              className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                            >
                              {hour < 12 ? "AM" : "PM"}
                            </div>
                          </div>

                          {/* Content Column */}
                          <div className="flex-1 min-w-0 h-8">
                            {hourEvents.length > 0 ? (
                              <div className="flex gap-1 h-full">
                                {hourEvents.map(
                                  (event: any, eventIdx: number) => {
                                    const eventTime = new Date(event.start_time);
                                    const isPast = eventTime <= new Date();
                                    const isCurrent =
                                      selectedDay.toDateString() ===
                                      new Date().toDateString() &&
                                      currentTime.getHours() === hour &&
                                      Math.abs(
                                        currentTime.getMinutes() -
                                        eventTime.getMinutes()
                                      ) <= 30;

                                    return (
                                      <div
                                        key={eventIdx}
                                        className={`relative overflow-hidden rounded-md border transition-all duration-300 hover:scale-[1.01] h-full flex-1 min-w-0 cursor-pointer ${isCurrent
                                          ? darkMode
                                            ? "bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20"
                                            : "bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                                          : isPast
                                            ? darkMode
                                              ? "bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20"
                                              : "bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                                            : darkMode
                                              ? "bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20"
                                              : "bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                                          }`}
                                        onClick={() => handleMeetingClick(event)}
                                      >
                                        {/* Status Indicator */}
                                        <div
                                          className={`absolute top-0 left-0 w-1 h-full ${isCurrent
                                            ? "bg-blue-500"
                                            : isPast
                                              ? "bg-green-500"
                                              : "bg-purple-900"
                                            }`}
                                        ></div>

                                        <div className="p-2 pl-3 h-full flex items-center">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1">
                                              <div
                                                className={`font-semibold text-xs truncate ${darkMode
                                                  ? "text-white"
                                                  : "text-gray-900"
                                                  }`}
                                              >

                                                {event.title || "Untitled Meeting"}
                                              </div>
                                              {(event.is_recurring || event.is_recurring_instance) && (
                                                <svg
                                                  className={`w-3 h-3 ${darkMode ? "text-purple-300" : "text-purple-600"
                                                    }`}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                  />
                                                </svg>
                                              )}
                                            </div>
                                            <div
                                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                                }`}
                                            >
                                              {new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            ) : (
                              <div
                                className={`h-full ${darkMode ? "bg-white/5" : "bg-gray-50"
                                  } rounded-md opacity-40`}
                              ></div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {viewMode === "day" && (
            <>
              {/* MOBILE DAY VIEW */}
              <div className="sm:hidden">
                {getDayHours().map((hour) => {
                  const dateStr = `${selectedDay.getFullYear()}-${String(
                    selectedDay.getMonth() + 1
                  ).padStart(2, "0")}-${String(selectedDay.getDate()).padStart(2, "0")}`;

                  const dayEvents = meetingEvents[dateStr] || [];
                  const hourEvents = dayEvents.filter((event) => {
                    if (!event.start_time || !event.meeting_date) return false;
                    const eventDateTime = new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`);
                    return eventDateTime.getHours() === hour;
                  });

                  return (
                    <div key={hour} className="border border-white/20 rounded-xl p-3 mb-3 bg-white/5">
                      {/* Time */}
                      <div className="text-sm font-semibold mb-1 text-white/70">
                        {formatHour24(hour)} {hour < 12 ? "AM" : "PM"}
                      </div>

                      {/* EVENTS COLUMN LIST (NO OVERLAP EVER) */}
                      <div className="flex flex-col gap-2">
                        {hourEvents.length > 0 ? (
                          hourEvents.map((event, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleMeetingClick(event)}
                              className="w-full p-3 rounded-lg border bg-indigo-500/10 border-indigo-500/40 flex flex-col gap-1"
                            >
                              <div className="text-xs font-semibold text-white truncate">
                                {event.title || "Untitled Meeting"}
                              </div>

                              <div className="text-[11px] text-white/60">
                                {new Date(`${event.meeting_date.split("T")[0]}T${event.start_time}`)
                                  .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </div>

                              <div className="flex justify-between items-center">
                                <span className="px-2 py-1 text-[10px] rounded-full bg-purple-600/30 text-purple-200">
                                  Upcoming
                                </span>

                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="w-full h-10 bg-white/10 rounded-md opacity-40"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </>
          )}

        </div>
        {/* Meeting Details Modal */}
        {showMeetingModal && selectedDate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div
              className={`${darkMode ? "bg-[#232336]" : "bg-white"
                } rounded-3xl shadow-2xl w-full max-w-3xl relative border ${darkMode ? "border-white/10" : "border-gray-200"
                } overflow-hidden`}
            >
              {/* Header */}
              <div
                className={`p-6 border-b ${darkMode ? "border-white/10" : "border-gray-200"
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <h2
                        className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {new Date(selectedDate).toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                        })}
                      </h2>
                      <p
                        className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        {new Date(selectedDate).toLocaleDateString("en-US", {
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                  <button
                    className={`p-2 rounded-xl transition-all duration-200 self-start ${darkMode
                      ? "text-white/60 hover:text-white hover:bg-white/10"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label="Close"
                    onClick={() => setShowMeetingModal(false)}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div
                    className={`text-sm font-medium ${darkMode ? "text-white/80" : "text-gray-700"
                      }`}
                  >
                    {(meetingEvents[selectedDate] || []).length} meeting
                    {(meetingEvents[selectedDate] || []).length !== 1
                      ? "s"
                      : ""}{" "}
                    scheduled
                  </div>
                  <button
                    onClick={() => {
                      setShowMeetingModal(false);
                      setShowCreateMeeting(true);
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${darkMode
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    + Add Meeting
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-96 overflow-y-auto scrollbar-hide">
                {(meetingEvents[selectedDate] || []).length > 0 ? (
                  <div className="space-y-3">
                    {(meetingEvents[selectedDate] || []).map((meeting, idx) => {
                      // Format time from HH:mm:ss to HH:mm AM/PM
                      const formatTime = (timeString: string) => {
                        if (!timeString) return '';
                        const [hours, minutes] = timeString.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour % 12 || 12;
                        return `${displayHour}:${minutes} ${ampm}`;
                      };

                      // Get status badge styling
                      const getStatusBadge = (status: string) => {
                        const statusConfig = {
                          scheduled: {
                            label: 'Scheduled',
                            className: 'bg-green-500/20 text-green-400 border-green-500/30'
                          },
                          rescheduled: {
                            label: 'Rescheduled',
                            className: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                          },
                          completed: {
                            label: 'Completed',
                            className: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                          },
                          cancelled: {
                            label: 'Cancelled',
                            className: 'bg-red-500/20 text-red-400 border-red-500/30'
                          }
                        } as const;

                        if (status in statusConfig) {
                          return statusConfig[status as keyof typeof statusConfig];
                        }

                        return statusConfig.scheduled;
                      };


                      const statusBadge = getStatusBadge(meeting.status || 'scheduled');
                      const startTime = formatTime(meeting.start_time || '00:00:00');
                      const endTime = formatTime(meeting.end_time || '00:00:00');

                      return (
                        <div
                          key={meeting.id || idx}
                          className={`rounded-2xl border transition-all duration-300 hover:shadow-xl cursor-pointer overflow-hidden ${darkMode
                            ? "bg-gradient-to-br from-[#2c2c3a] to-[#1a1a2e] border-white/10 hover:border-white/20"
                            : "bg-gradient-to-br from-white to-gray-50 border-gray-200 hover:border-gray-300"
                            }`}
                          onClick={() => {
                            setSelectedMeetingForDetails(meeting);
                            setShowMeetingDetails(true);
                            setShowMeetingModal(false);
                          }}
                        >
                          <div className="p-5">
                            {/* Header Section */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1 min-w-0">
                                <h3 className={`text-lg font-bold mb-2 flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-900"
                                  }`}>
                                  <span className="truncate">{meeting.title || "Untitled Meeting"}</span>
                                  {(meeting.is_recurring || meeting.is_recurring_instance) && (
                                    <svg
                                      className="w-4 h-4 text-purple-400 flex-shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                  )}
                                </h3>

                                {/* Time Display */}
                                <div className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                                  }`}>
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  <span>
                                    {startTime && endTime
                                      ? `${startTime} - ${endTime}`
                                      : "No time set"}
                                  </span>
                                </div>
                              </div>

                              {/* Status Badge */}
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ml-3 ${statusBadge.className}`}>
                                {statusBadge.label}
                              </span>
                            </div>

                            {/* Host Information */}
                            {meeting.created_by && (
                              <div className={`flex items-center gap-2 mb-4 text-sm ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}>
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                                <span>
                                  {meeting.created_by.first_name} {meeting.created_by.last_name}
                                </span>
                              </div>
                            )}

                            {/* Join Button */}
                            <button
                              className={`w-full ${meeting.completed_status || meeting.status === 'completed'
                                ? darkMode
                                  ? "bg-gray-600 cursor-not-allowed text-gray-400"
                                  : "bg-gray-400 cursor-not-allowed text-gray-200"
                                : "bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white"
                                } font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform ${meeting.completed_status || meeting.status === 'completed' ? "" : "hover:scale-[1.02]"
                                } flex items-center justify-center gap-2`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!meeting.completed_status && meeting.status !== 'completed') {
                                  handleJoinMeeting(meeting);
                                }
                              }}
                              disabled={meeting.completed_status || meeting.status === 'completed'}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                              {meeting.completed_status || meeting.status === 'completed' ? 'Meeting Completed' : 'Join'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${darkMode ? "bg-white/10" : "bg-gray-100"
                        }`}
                    >
                      <CalendarIcon
                        className={`w-10 h-10 ${darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      No meetings scheduled
                    </h3>
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      Scheduled meetings will appear here
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Meeting Details Modal */}
        {showMeetingDetails && selectedMeetingForDetails && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden"
            onClick={() => setShowMeetingOptionsDropdown(false)}
          >
            <div
              className={`rounded-3xl shadow-2xl border w-[95vw] max-w-3xl relative max-h-[100vh] overflow-hidden ${darkMode
                ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
                : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
                }`}
            >
              {/* Top Bar with Tabs and Buttons */}
              <div className="relative px-8 pt-10 pb-6">
                <div
                  className={`flex items-center ${isEditing ? "justify-between" : "justify-between"
                    }`}
                >
                  {/* Left side - Title or Tabs */}
                  <div className="flex-1">
                    {isEditing ? (
                      /* Tabs - Only show when editing */
                      <div className="max-w-xl">
                        <div
                          className={`flex gap-1 rounded-xl p-1 ${darkMode ? "bg-white/5" : "bg-gray-100"
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => setMeetingDetailsTab("details")}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${meetingDetailsTab === "details"
                              ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                              : darkMode
                                ? "text-white/60 hover:text-white hover:bg-white/5"
                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                              }`}
                          >
                            Details
                          </button>
                          <button
                            type="button"
                            onClick={() => setMeetingDetailsTab("security")}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${meetingDetailsTab === "security"
                              ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                              : darkMode
                                ? "text-white/60 hover:text-white hover:bg-white/5"
                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                              }`}
                          >
                            Security
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Meeting Title and Time - Show when not editing */
                      <div className="text-left">
                        <h2
                          className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          {selectedMeetingForDetails.title ||
                            "Untitled Meeting"}
                        </h2>
                        <div
                          className={`flex items-center gap-2 mt-1 text-base ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span>
                            {formatDate(selectedMeetingForDetails.meeting_date)} -{" "}
                            {selectedMeetingForDetails.start_time
                              ? formatTime(selectedMeetingForDetails.meeting_date.split('T')[0] + 'T' + selectedMeetingForDetails.start_time)
                              : "9:00 AM - 10:00 AM"}
                          </span>
                        </div>

                        {/* Recurring Meeting Indicator */}
                        {(selectedMeetingForDetails.is_recurring || selectedMeetingForDetails.is_recurring_instance) && (
                          <div className={`flex items-center gap-2 mt-2 text-sm ${darkMode ? "text-purple-300" : "text-purple-600"
                            }`}>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                            <span>
                              {selectedMeetingForDetails.is_recurring ? "Recurring Meeting" : "Recurring Instance"}
                              {selectedMeetingForDetails.repeat_status && (
                                <span className="ml-1 capitalize">
                                  ({selectedMeetingForDetails.repeat_status})
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right Side Buttons - Always visible */}
                  <div className="flex items-center gap-2">
                    {/* More Options Menu - Only show for meetings created by user */}
                    {selectedMeetingForDetails.createdByMe && (
                      <div
                        className="relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                            ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                            }`}
                          aria-label="More options"
                          onClick={() =>
                            setShowMeetingOptionsDropdown(
                              !showMeetingOptionsDropdown
                            )
                          }
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                        {showMeetingOptionsDropdown && (
                          <div
                            className={`absolute right-0 top-full -mt-0.5 w-48 rounded-xl shadow-2xl border p-2 z-50 ${darkMode
                              ? "bg-[#232336] border-white/10"
                              : "bg-white border-gray-200"
                              }`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                handleEditClick();
                                setShowMeetingOptionsDropdown(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${darkMode
                                ? "text-white/80 hover:bg-white/10 hover:text-white"
                                : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                                }`}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              Edit Meeting
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCancelMeetingModal(true);
                                setShowMeetingOptionsDropdown(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${darkMode
                                ? "text-red-400 hover:bg-red-600/20 hover:text-red-300"
                                : "text-red-600 hover:bg-red-50 hover:text-red-700"
                                }`}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                              </svg>
                              Cancel Meeting
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Close Button */}
                    <button
                      className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                        ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                        }`}
                      aria-label="Close"
                      onClick={() => setShowMeetingDetails(false)}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              <div className="px-8 pb-6 overflow-y-auto max-h-[50vh] scrollbar-hide">
                {(meetingDetailsTab === "details" || !isEditing) && (
                  <div className="space-y-4">
                    {isEditing && (
                      <>
                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Title
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <svg
                              className={`w-4 h-4 ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <input
                              type="text"
                              value={editForm.title}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  title: e.target.value,
                                })
                              }
                              className={`flex-1 text-lg font-medium bg-transparent border-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                ? "text-white placeholder-white/40"
                                : "text-gray-900 placeholder-gray-500"
                                }`}
                              placeholder="Meeting Title"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Meeting Info Grid */}
                    {!isEditing && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Host
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <svg
                              className={`w-4 h-4 ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {selectedMeetingForDetails.created_by.first_name || "No host set"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Room
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <svg
                              className={`w-4 h-4 ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                              />
                            </svg>
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {selectedMeetingForDetails.id || "No room set"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Status
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <svg
                              className={`w-4 h-4 ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                Scheduled
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Recurring
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <svg
                              className={`w-4 h-4 ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {selectedMeetingForDetails.repeat_status || "No recurring set"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <div className="mb-2">
                        <span
                          className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                            }`}
                        >
                          Description
                        </span>
                      </div>
                      <div
                        className={`rounded-lg p-3 ${darkMode
                          ? "bg-white/5 border border-white/10"
                          : "bg-gray-50 border border-gray-200"
                          }`}
                      >
                        <p
                          className={`text-sm leading-relaxed ${darkMode ? "text-white/80" : "text-gray-700"
                            }`}
                        >
                          {selectedMeetingForDetails.description || "No description set"}
                        </p>
                      </div>
                    </div>

                    {/* Participants */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                            }`}
                        >
                          Participants (3)
                        </span>
                        {selectedMeetingForDetails.createdByMe && (
                          <button
                            className={`px-2 py-1 text-xs rounded transition-all duration-200 ${darkMode
                              ? "bg-white/10 text-white hover:bg-white/20"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                          >
                            + Invite
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {selectedMeetingForDetails?.participantDetails?.map((participant: any, index: number) => (
                          <div
                            key={index}
                            className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-white/5 border border-white/10" : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <img
                              src={`https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png`}
                              alt={participant.email}
                              className={`w-8 h-8 rounded-full object-cover ${darkMode ? "border border-white/20" : "border border-gray-300"
                                }`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-gray-900"
                                  }`}>
                                  {participant.user
                                    ? `${participant.user.first_name} ${participant.user.last_name}`
                                    : participant.email}
                                </span>
                              </div>
                              <div className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}>
                                {participant.email}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {participant.accepted ? (
                                <span className={`px-1.5 py-0.5 text-xs rounded-md flex items-center gap-1 ${darkMode
                                  ? "bg-green-600/20 text-green-400 border border-green-400/30"
                                  : "bg-green-50 text-green-700 border border-green-200"
                                  }`}>
                                  <CheckCircle size={10} className="flex-shrink-0" />
                                  <span className="hidden sm:inline font-medium">Accepted</span>
                                </span>
                              ) : (
                                <span className={`px-1.5 py-0.5 text-xs rounded-md flex items-center gap-1 ${darkMode
                                  ? "bg-blue-600/20 text-blue-400 border border-blue-400/30"
                                  : "bg-blue-50 text-blue-700 border border-blue-200"
                                  }`}>
                                  <AlertCircle size={10} className="flex-shrink-0" />
                                  <span className="hidden sm:inline font-medium">Pending</span>
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {meetingDetailsTab === "security" && isEditing && (
                  <div className="space-y-4">
                    {/* Security Settings */}
                    <div
                      className={`rounded-xl p-4 border ${darkMode
                        ? "bg-white/5 border-white/10"
                        : "bg-gray-50 border-gray-200"
                        }`}
                    >
                      <div className="space-y-4">
                        {/* Password Protection */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h4
                              className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              Password Protection
                            </h4>
                            <p
                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            >
                              Require a password to join the meeting
                            </p>
                          </div>
                          <button
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.requirePassword
                              ? "bg-gradient-to-r from-pink-500 to-red-500"
                              : darkMode
                                ? "bg-white/20"
                                : "bg-gray-300"
                              }`}
                            onClick={() =>
                              setSecuritySettings({
                                ...securitySettings,
                                requirePassword:
                                  !securitySettings.requirePassword,
                              })
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.requirePassword
                                ? "translate-x-6"
                                : "translate-x-1"
                                } ${darkMode ? "bg-white" : "bg-white shadow-sm"
                                }`}
                            />
                          </button>
                        </div>

                        {securitySettings.requirePassword && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <label
                                className={`text-sm font-medium whitespace-nowrap ${darkMode ? "text-white/70" : "text-gray-700"
                                  }`}
                              >
                                Meeting Password
                              </label>
                              <div className="w-64 relative">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Meeting password will be generated..."
                                  className={`w-full rounded-lg px-3 py-2 pr-20 transition-all duration-200 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                    ? "bg-white/10 border border-white/20 text-white placeholder-white/40"
                                    : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                                    }`}
                                  value={securitySettings.password}
                                  readOnly
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${darkMode
                                    ? "text-white/60 hover:text-white"
                                    : "text-gray-400 hover:text-gray-600"
                                    }`}
                                >
                                  {showPassword ? (
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Waiting Room */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h4
                              className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              Waiting Room
                            </h4>
                            <p
                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            >
                              Participants must be admitted by the host
                            </p>
                          </div>
                          <button
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.waitingRoom
                              ? "bg-gradient-to-r from-pink-500 to-red-500"
                              : darkMode
                                ? "bg-white/20"
                                : "bg-gray-300"
                              }`}
                            onClick={() =>
                              setSecuritySettings({
                                ...securitySettings,
                                waitingRoom: !securitySettings.waitingRoom,
                              })
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.waitingRoom
                                ? "translate-x-6"
                                : "translate-x-1"
                                } ${darkMode ? "bg-white" : "bg-white shadow-sm"
                                }`}
                            />
                          </button>
                        </div>

                        {/* Mute on Entry */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h4
                              className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              Mute on Entry
                            </h4>
                            <p
                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            >
                              Automatically mute participants when they join
                            </p>
                          </div>
                          <button
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.muteOnEntry
                              ? "bg-gradient-to-r from-pink-500 to-red-500"
                              : darkMode
                                ? "bg-white/20"
                                : "bg-gray-300"
                              }`}
                            onClick={() =>
                              setSecuritySettings({
                                ...securitySettings,
                                muteOnEntry: !securitySettings.muteOnEntry,
                              })
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.muteOnEntry
                                ? "translate-x-6"
                                : "translate-x-1"
                                } ${darkMode ? "bg-white" : "bg-white shadow-sm"
                                }`}
                            />
                          </button>
                        </div>

                        {/* Allow Recording */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h4
                              className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              Allow Recording
                            </h4>
                            <p
                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            >
                              Participants can record the meeting
                            </p>
                          </div>
                          <button
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.allowRecording
                              ? "bg-gradient-to-r from-pink-500 to-red-500"
                              : darkMode
                                ? "bg-white/20"
                                : "bg-gray-300"
                              }`}
                            onClick={() =>
                              setSecuritySettings({
                                ...securitySettings,
                                allowRecording:
                                  !securitySettings.allowRecording,
                              })
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.allowRecording
                                ? "translate-x-6"
                                : "translate-x-1"
                                } ${darkMode ? "bg-white" : "bg-white shadow-sm"
                                }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Action Buttons */}
              <div className="px-8 pb-8">
                <div className="flex items-center gap-3">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSaveEdit}
                        className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${darkMode
                          ? "bg-white/10 text-white hover:bg-white/20"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className={`w-full ${selectedMeetingForDetails.completed_status || selectedMeetingForDetails.status === 'completed'
                        ? darkMode
                          ? "bg-gray-600 cursor-not-allowed text-gray-400"
                          : "bg-gray-400 cursor-not-allowed text-gray-200"
                        : "bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white"
                        } font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform ${selectedMeetingForDetails.completed_status || selectedMeetingForDetails.status === 'completed' ? "" : "hover:scale-[1.02]"
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!selectedMeetingForDetails.completed_status && selectedMeetingForDetails.status !== 'completed') {
                          handleJoinMeeting(selectedMeetingForDetails);
                        }
                      }}
                      disabled={selectedMeetingForDetails.completed_status || selectedMeetingForDetails.status === 'completed'}
                    >
                      <div className="text-center">
                        {selectedMeetingForDetails.completed_status || selectedMeetingForDetails.status === 'completed' ? 'Meeting Completed' : 'Join Meeting'}
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Meeting Confirmation Modal */}
        {showCancelMeetingModal && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div
              className={`rounded-2xl shadow-2xl border w-full max-w-md mx-4 ${darkMode
                ? "bg-[#232336] border-white/10"
                : "bg-white border-gray-200"
                }`}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3
                      className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Cancel Meeting
                    </h3>
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      Are you sure you want to cancel this meeting?
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelMeeting}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-lg"
                  >
                    Cancel Meeting
                  </button>
                  <button
                    onClick={() => setShowCancelMeetingModal(false)}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${darkMode
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    Keep Meeting
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Meeting Modal */}
        {showCreateMeeting && (
          <CreateMeeting
            onClose={() => setShowCreateMeeting(false)}
            onCreated={(meeting) => {
              setShowCreateMeeting(false);
              // Refresh meetings after creation
              window.location.reload();
            }}
            darkMode={darkMode}
          />
        )}
      </main>
      <ProfileModal
        open={showProfile}
        onClose={() => setShowProfile(false)}
        user={{
          name: "Ledy Sintia",
          email: "ledy.sintia@email.com",
          avatar: "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png",
        }}
        onLogout={() => {
          setShowProfile(false);
        }}
      />
    </div>
  );
};

export default Calendar;
